import * as React from 'react';
import { EditorTool } from '../../../../shared/TileEditorTypes';

interface ToolbarProps {
  currentTool: EditorTool;
  onToolChange: (tool: EditorTool) => void;
  gridVisible: boolean;
  onGridToggle: () => void;
  snapToGrid: boolean;
  onSnapToggle: () => void;
  showCollision: boolean;
  onCollisionToggle: () => void;
  onNewMap: () => void;
  onLoadMap: () => void;
  onSave: () => void;
  onTest: () => void;
  onExit: () => void;
  // Entity, Teleporter, and Boundary windows
  onOpenBoundaryControl: () => void;
  onOpenOptions: () => void;
  onOpenShortcuts: () => void;
  // Cloud Sync Status
  syncStatus?: 'synced' | 'local' | 'saving' | 'error' | 'idle';
  isCloudAvailable?: boolean;
  // History
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  // Metadata
  mapName?: string;
}

export const Toolbar: React.FC<ToolbarProps> = ({
  currentTool,
  onToolChange,
  gridVisible,
  onGridToggle,
  snapToGrid,
  onSnapToggle,
  showCollision,
  onCollisionToggle,
  onNewMap,
  onLoadMap,
  onSave,
  onTest,
  onExit,
  onOpenBoundaryControl,
  onOpenOptions,
  onOpenShortcuts,
  syncStatus = 'idle',
  isCloudAvailable = false,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  mapName = 'Unknown Map'
}) => {
  // Toolset with Select for PropertiesPanel integration
  const tools = [
    { id: EditorTool.SELECT, label: 'Select', icon: '🔍' },
    { id: EditorTool.BRUSH, label: 'Brush', icon: '🖌️' },
    { id: EditorTool.ERASER, label: 'Eraser', icon: '🧽' },
    { id: EditorTool.FILL, label: 'Fill', icon: '🪣' },
    { id: EditorTool.RECTANGLE, label: 'Rect', icon: '⬜' },
    { id: EditorTool.LINE, label: 'Line', icon: '📏' },
    { id: EditorTool.EYEDROPPER, label: 'Pick', icon: '💧' },
  ];

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: '#2c3e50',
      color: 'white',
      padding: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      borderBottom: '1px solid #34495e',
      zIndex: 1000,
      boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', marginRight: '20px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
          🎮 Level Editor
        </div>
        <div style={{ fontSize: '12px', color: '#bdc3c7', marginTop: '2px' }}>
          Map: {mapName}
        </div>
      </div>

      {/* History Controls */}
      <div style={{ display: 'flex', gap: '5px', marginRight: '10px' }}>
        <button
          onClick={onUndo}
          disabled={!canUndo}
          style={{
            padding: '8px',
            backgroundColor: '#34495e',
            color: canUndo ? 'white' : '#7f8c8d',
            border: '1px solid #2c3e50',
            borderRadius: '4px',
            cursor: canUndo ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: canUndo ? 1 : 0.5
          }}
          title="Undo (Ctrl+Z)"
        >
          ↩️
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          style={{
            padding: '8px',
            backgroundColor: '#34495e',
            color: canRedo ? 'white' : '#7f8c8d',
            border: '1px solid #2c3e50',
            borderRadius: '4px',
            cursor: canRedo ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: canRedo ? 1 : 0.5
          }}
          title="Redo (Ctrl+Y)"
        >
          ↪️
        </button>
      </div>

      {/* Tools */}
      <div style={{ display: 'flex', gap: '5px', marginRight: '20px' }}>
        {tools.map(tool => (
          <button
            key={tool.id}
            onClick={() => onToolChange(tool.id)}
            style={{
              padding: '8px 12px',
              backgroundColor: currentTool === tool.id ? '#3498db' : '#34495e',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
            title={tool.label}
          >
            <span>{tool.icon}</span>
            <span>{tool.label}</span>
          </button>
        ))}
      </div>

      {/* View Controls */}
      <div style={{ display: 'flex', gap: '5px', marginRight: '20px' }}>
        <button
          onClick={onGridToggle}
          style={{
            padding: '8px 12px',
            backgroundColor: gridVisible ? '#27ae60' : '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title="Toggle Grid"
        >
          📐 Grid
        </button>

        <button
          onClick={onSnapToggle}
          style={{
            padding: '8px 12px',
            backgroundColor: snapToGrid ? '#27ae60' : '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title="Toggle Snap to Grid"
        >
          ⚡ Snap
        </button>

        <button
          onClick={onCollisionToggle}
          style={{
            padding: '8px 12px',
            backgroundColor: showCollision ? '#27ae60' : '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title="Toggle Collision Visualization"
        >
          🛡️ Collision
        </button>
      </div>

      {/* Boundary Window */}
      <div style={{ display: 'flex', gap: '5px', marginRight: '10px' }}>
        <button
          onClick={onOpenBoundaryControl}
          style={{
            padding: '8px 12px',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title="Configure Map Boundaries"
        >
          🚧 Boundaries
        </button>

        <button
          onClick={onOpenOptions}
          style={{
            padding: '8px 12px',
            backgroundColor: '#34495e',
            color: 'white',
            border: '1px solid #7f8c8d',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title="Editor Navigation Options"
        >
          ⚙️ Options
        </button>

        <button
          onClick={onOpenShortcuts}
          style={{
            padding: '8px 12px',
            backgroundColor: '#34495e',
            color: 'white',
            border: '1px solid #7f8c8d',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title="Keyboard Shortcuts"
        >
          ❓ Help
        </button>
      </div>

      {/* Cloud Status Indicator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '5px 12px',
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderRadius: '20px',
        fontSize: '12px',
        color: isCloudAvailable ? '#fff' : '#888'
      }}>
        <span style={{
          fontSize: '14px',
          color: syncStatus === 'synced' ? '#27ae60' :
                 syncStatus === 'local' ? '#f39c12' :
                 syncStatus === 'saving' ? '#3498db' :
                 syncStatus === 'error' ? '#e74c3c' : '#888'
        }}>
          {syncStatus === 'synced' ? '☁️' :
           syncStatus === 'local' ? '📂' :
           syncStatus === 'saving' ? '⏳' :
           syncStatus === 'error' ? '❌' : '☁️'}
        </span>
        <span style={{ fontWeight: 'bold' }}>
          {syncStatus === 'synced' ? 'Synced to Cloud' :
           syncStatus === 'local' ? 'Saved Locally' :
           syncStatus === 'saving' ? 'Saving...' :
           syncStatus === 'error' ? 'Sync Error' :
           isCloudAvailable ? 'Cloud Ready' : 'Offline Mode'}
        </span>
      </div>

      {/* Action Buttons - Simplified */}
      <div style={{ display: 'flex', gap: '5px', marginLeft: 'auto' }}>
        <button
          onClick={onNewMap}
          style={{
            padding: '8px 16px',
            backgroundColor: '#16a085',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontWeight: 'bold'
          }}
          title="Create New Level"
        >
          📄 New
        </button>

        <button
          onClick={onLoadMap}
          style={{
            padding: '8px 16px',
            backgroundColor: '#8e44ad',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontWeight: 'bold'
          }}
          title="Load Map"
        >
          📂 Load
        </button>

        <button
          onClick={onSave}
          style={{
            padding: '8px 16px',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontWeight: 'bold'
          }}
          title="Save Level"
        >
          💾 Save
        </button>

        <button
          onClick={onTest}
          style={{
            padding: '8px 16px',
            backgroundColor: '#9b59b6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontWeight: 'bold'
          }}
          title="Test Level"
        >
          🎮 Test
        </button>

        <button
          onClick={onExit}
          style={{
            padding: '8px 16px',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            marginLeft: '10px',
            fontWeight: 'bold'
          }}
          title="Back to Game"
        >
          🚪 Exit
        </button>
      </div>
    </div>
  );
};
